'use client';

import { useState, useMemo } from 'react';

import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';
import LivePriceIndicator from '@/components/common/LivePriceIndicator';
import AffiliateDisclosure from '@/components/common/AffiliateDisclosure';
import type { HolidaySearchParams } from './HolidaySearchInteractive';
import { trackEvent } from '@/lib/analytics';

interface HolidayResultsProps {
  searchParams: HolidaySearchParams;
}

const HolidayResults = ({ searchParams }: HolidayResultsProps) => {
  const [sortBy, setSortBy] = useState('recommended');

  // Mock holiday package data - In production, this would come from API
  const packages = useMemo(() => [
  {
    id: '1',
    title: 'Tropical Paradise - Maldives',
    destination: 'Maldives',
    duration: '7 Days / 6 Nights',
    image: "https://images.unsplash.com/photo-1683650904081-78afc08950cb",
    alt: 'Overwater bungalows in Maldives with crystal clear turquoise water',
    rating: 4.9,
    reviewCount: 342,
    price: 2899,
    originalPrice: 3599,
    includes: ['Round-trip Flights', '5-Star Resort', 'All Meals', 'Water Sports', 'Spa Treatment'],
    highlights: ['Private Beach Access', 'Overwater Villa', 'Snorkeling Tours', 'Sunset Cruise'],
    availability: 'high',
    pnrGuaranteed: true
  },
  {
    id: '2',
    title: 'European Adventure - Italy & France',
    destination: 'Europe',
    duration: '10 Days / 9 Nights',
    image: "https://images.unsplash.com/photo-1508950234461-f11e43691ca1",
    alt: 'Eiffel Tower in Paris with sunset sky and city lights',
    rating: 4.8,
    reviewCount: 567,
    price: 3499,
    originalPrice: 4299,
    includes: ['Round-trip Flights', '4-Star Hotels', 'Daily Breakfast', 'City Tours', 'Train Tickets'],
    highlights: ['Rome Colosseum', 'Venice Gondola', 'Paris Eiffel Tower', 'Wine Tasting'],
    availability: 'medium',
    pnrGuaranteed: true
  },
  {
    id: '3',
    title: 'Safari Adventure - Kenya',
    destination: 'Kenya',
    duration: '8 Days / 7 Nights',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1c3d08c08-1767167839808.png",
    alt: 'African safari landscape with elephants and acacia trees at sunset',
    rating: 4.9,
    reviewCount: 289,
    price: 3199,
    originalPrice: null,
    includes: ['Round-trip Flights', 'Safari Lodge', 'All Meals', 'Game Drives', 'Park Fees'],
    highlights: ['Big Five Safari', 'Masai Mara', 'Hot Air Balloon', 'Cultural Village Visit'],
    availability: 'low',
    pnrGuaranteed: true
  },
  {
    id: '4',
    title: 'Asian Discovery - Thailand & Vietnam',
    destination: 'Southeast Asia',
    duration: '12 Days / 11 Nights',
    image: "https://images.unsplash.com/photo-1663074958903-879f50268f6a",
    alt: 'Traditional Thai temple with golden spires and ornate architecture',
    rating: 4.7,
    reviewCount: 421,
    price: 2599,
    originalPrice: 3199,
    includes: ['Round-trip Flights', 'Boutique Hotels', 'Daily Breakfast', 'Tours', 'Internal Flights'],
    highlights: ['Bangkok Temples', 'Halong Bay Cruise', 'Cooking Class', 'Beach Time'],
    availability: 'high',
    pnrGuaranteed: true
  },
  {
    id: '5',
    title: 'Caribbean Escape - Jamaica',
    destination: 'Jamaica',
    duration: '5 Days / 4 Nights',
    image: "https://images.unsplash.com/photo-1722697798805-0500b5972549",
    alt: 'Caribbean beach with white sand, palm trees, and turquoise water',
    rating: 4.8,
    reviewCount: 198,
    price: 1899,
    originalPrice: 2399,
    includes: ['Round-trip Flights', 'All-Inclusive Resort', 'All Meals & Drinks', 'Water Sports', 'Entertainment'],
    highlights: ['Private Beach', 'Dunn\'s River Falls', 'Reggae Night', 'Snorkeling'],
    availability: 'high',
    pnrGuaranteed: true
  },
  {
    id: '6',
    title: 'Mountain Retreat - Swiss Alps',
    destination: 'Switzerland',
    duration: '6 Days / 5 Nights',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_130b8c634-1772215512146.png",
    alt: 'Swiss Alps mountain landscape with snow-capped peaks and alpine village',
    rating: 4.9,
    reviewCount: 312,
    price: 2799,
    originalPrice: null,
    includes: ['Round-trip Flights', 'Mountain Chalet', 'Daily Breakfast', 'Ski Pass', 'Cable Car Tickets'],
    highlights: ['Skiing & Snowboarding', 'Mountain Views', 'Chocolate Factory', 'Scenic Train'],
    availability: 'medium',
    pnrGuaranteed: true
  }], []);

  const getAvailabilityColor = (availability: string) => {
    switch (availability) {
      case 'high':
        return 'text-success';
      case 'medium':
        return 'text-warning';
      case 'low':
        return 'text-error';
      default:
        return 'text-muted-foreground';
    }
  };

  const getAvailabilityText = (availability: string) => {
    switch (availability) {
      case 'high':
        return 'High Availability';
      case 'medium':
        return 'Limited Spots';
      case 'low':
        return 'Almost Full';
      default:
        return 'Check Availability';
    }
  };

  const handleBookingClick = (packageId: string, price: number, destination: string, duration: string) => {
    trackEvent('holiday_lead_request', {
      package_id: packageId,
      price: price,
      destination: destination
    });

    // Redirect to booking interest form
    const params = new URLSearchParams({
      type: 'holiday',
      destination: destination,
      price: price.toString()
    });
    window.location.href = `/booking-interest?${params.toString()}`;
  };

  const handleDirectBooking = (packageId: string, title: string, price: number, destination: string, duration: string) => {
    trackEvent('holiday_direct_booking', {
      package_id: packageId,
      title: title,
      price: price
    });

    // Redirect to booking page with package details
    const params = new URLSearchParams({
      type: 'holiday',
      packageId: packageId,
      packageName: title,
      destination: destination,
      duration: duration,
      price: price.toString()
    });
    window.location.href = `/booking?${params.toString()}`;
  };

  return (
    <div className="space-y-6">
      {/* Results Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-card rounded-lg shadow-brand p-4">
        <div>
          <h2 className="font-heading text-xl font-bold text-foreground">
            {packages.length} Holiday Packages Found
          </h2>
          <p className="text-sm text-muted-foreground">
            {searchParams.destination && `for ${searchParams.destination}`}
          </p>
        </div>
        <div className="flex items-center space-x-2">
          <span className="text-sm text-muted-foreground font-bold">Sort by:</span>
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="px-4 py-2 border-2 border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent font-bold text-foreground">
            
            <option value="recommended">Recommended</option>
            <option value="price-low">Price: Low to High</option>
            <option value="price-high">Price: High to Low</option>
            <option value="rating">Highest Rated</option>
            <option value="duration">Duration</option>
          </select>
        </div>
      </div>

      {/* Affiliate Disclosure */}
      <AffiliateDisclosure partner="Expedia" />

      {/* Package Cards */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {packages.map((pkg) =>
        <div
          key={pkg.id}
          className="bg-card rounded-lg shadow-brand hover:shadow-brand-lg transition-all duration-300 overflow-hidden group">
          
            <div className="relative h-64 overflow-hidden">
              <AppImage
              src={pkg.image}
              alt={pkg.alt}
              className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
            
              
              {pkg.pnrGuaranteed &&
            <div className="absolute top-4 left-4 bg-primary text-white px-3 py-1.5 rounded-full text-xs font-heading font-semibold flex items-center space-x-1.5">
                  <Icon name="ShieldCheckIcon" size={14} variant="solid" />
                  <span>PNR Guaranteed</span>
                </div>
            }

              {pkg.originalPrice &&
            <div className="absolute top-4 right-4 bg-coral text-white px-3 py-1.5 rounded-full text-xs font-heading font-semibold">
                  Save ${pkg.originalPrice - pkg.price}
                </div>
            }

              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-4">
                <div className="flex items-center space-x-2">
                  <Icon name="MapPinIcon" size={16} className="text-white" variant="solid" />
                  <span className="text-white text-sm font-medium">{pkg.destination}</span>
                  <span className="text-white/70 text-sm">•</span>
                  <span className="text-white text-sm font-medium">{pkg.duration}</span>
                </div>
              </div>
            </div>

            <div className="p-5">
              <div className="flex items-start justify-between mb-3">
                <h3 className="font-heading font-semibold text-lg text-foreground line-clamp-2 flex-1">
                  {pkg.title}
                </h3>
              </div>

              <div className="flex items-center space-x-4 mb-4">
                <div className="flex items-center space-x-1">
                  <Icon name="StarIcon" size={16} className="text-accent" variant="solid" />
                  <span className="text-sm font-semibold text-foreground">{pkg.rating}</span>
                  <span className="text-sm text-muted-foreground">({pkg.reviewCount})</span>
                </div>
              </div>

              <div className="space-y-2 mb-4">
                <h4 className="text-sm font-bold text-foreground">Package Includes:</h4>
                {pkg.includes.slice(0, 3).map((item, index) =>
              <div key={index} className="flex items-start space-x-2">
                    <Icon name="CheckCircleIcon" size={16} className="text-success mt-0.5 flex-shrink-0" variant="solid" />
                    <span className="text-sm text-foreground">{item}</span>
                  </div>
              )}
              </div>

              <div className="flex items-center justify-between pt-4 border-t border-border/30">
                <div>
                  <p className="text-sm text-muted-foreground mb-1">Total Package Price</p>
                  <div className="flex items-baseline gap-2">
                    {pkg.originalPrice &&
                  <span className="text-lg text-muted-foreground line-through">
                            ${pkg.originalPrice}
                          </span>
                  }
                    <span className="text-3xl font-heading font-black text-primary">
                      ${pkg.price}
                    </span>
                    <LivePriceIndicator
                    itemId={`holiday-${pkg.id}`}
                    basePrice={pkg.price}
                    volatility="low"
                    showTrend={true}
                    updateInterval={15000} />
                  
                  </div>
                  <p className="text-xs text-success font-semibold mt-1">
                    + £50 service fee
                  </p>
                </div>
                <div className="space-y-2">
                  <button
                  onClick={() => handleDirectBooking(pkg.id, pkg.title, pkg.price, pkg.destination, pkg.duration)}
                  className="bg-gradient-to-r from-primary to-secondary text-white font-heading font-black px-6 py-3 rounded-xl hover:shadow-xl transition-all duration-300 flex items-center gap-2 group">
                  
                    <Icon name="CreditCardIcon" size={20} />
                    <span>Book Now</span>
                    <Icon name="ArrowRightIcon" size={20} className="group-hover:translate-x-1 transition-transform" />
                  </button>
                  
                  <button
                  onClick={() => handleBookingClick(pkg.id, pkg.price, pkg.destination, pkg.duration)}
                  className="w-full px-6 py-3 border-2 border-primary text-primary font-semibold rounded-lg hover:bg-primary hover:text-white transition-all duration-300 flex items-center justify-center gap-2">
                  
                    <Icon name="ChatBubbleLeftRightIcon" size={20} />
                    <span>Request Quote</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>);

};

export default HolidayResults;